import React, { useState, useEffect, useContext, useMemo } from 'react'
import axios from '../../../config/axios'
import { Eye, FileCode, FileText, Award } from 'lucide-react'
import OrdersContext from '../../../contexts/OrdersContext'
import ModalContext from '../../../contexts/ModalContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import { OrdersDetailsDTO } from '../../../types/orders/orders-details'
import {
  PageHeader,
  StandardDataTable,
  StandardColumn,
  useColumnsState,
} from '../../../components/StandardPage'
import { Content } from '../Stock/style'
import { openSignedAttachment } from '../../../utils/openSignedAttachment'
import { downloadRemoteFile } from '../../../utils/downloadRemoteFile'

interface PloomesOrder {
  id: number
  title: string
  amount?: number
  stage?: {
    id: number
    name: string
  }
  person?: {
    name: string
  }
  win_time?: string
  other_properties?: any
  originDealId?: number
  statusId?: number
}

const ploomesOrdersScreenKey = 'ploomes_orders'

const ploomesOrderColumns: StandardColumn<PloomesOrder>[] = [
  { id: 'id', label: 'ID' },
  { id: 'title', label: 'Título', accessor: (o) => o.title || 'N/A' },
  { id: 'person', label: 'Cliente', accessor: (o) => o.person?.name || 'N/A' },
  {
    id: 'amount',
    label: 'Valor',
    accessor: (o) => o.amount ?? null,
    cell: (o) =>
      o.amount
        ? <>{`R$ ${o.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}</>
        : <>N/A</>,
    exportValue: (o) => o.amount ?? '',
  },
  { id: 'stage', label: 'Status', accessor: (o) => o.stage?.name || 'N/A' },
  {
    id: 'win_time',
    label: 'Data',
    accessor: (o) => (o.win_time ? new Date(o.win_time).getTime() : null),
    cell: (o) => <>{o.win_time ? new Date(o.win_time).toLocaleDateString('pt-BR') : 'N/A'}</>,
    exportValue: (o) => (o.win_time ? new Date(o.win_time).toLocaleDateString('pt-BR') : ''),
  },
]

const PloomesOrdersTable: React.FC = () => {
  const [orders, setOrders] = useState<PloomesOrder[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(false)

  const { setOrderId, setOriginDealId, setStatus, setStatusId } = useContext(OrdersContext)
  const { setModalActive, setModalSection } = useContext(ModalContext)
  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => ploomesOrderColumns, [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    ploomesOrdersScreenKey,
    columns,
  )

  useEffect(() => {
    const fetchPloomesOrders = async () => {
      try {
        setLoading(true)
        const response = await axios.post(`${process.env.REACT_APP_ORDERS_BASE_URL}/orders-by-ploomes/list`)
        setOrders(response.data || [])
        setError(null)
      } catch (err: any) {
        console.error('Erro ao buscar pedidos do Ploomes:', err)
        setError('Erro ao carregar pedidos do Ploomes')
      } finally {
        setLoading(false)
      }
    }

    fetchPloomesOrders()
  }, [refreshToken])

  const handleOrderClick = (order: PloomesOrder) => {
    setOrderId(order.id)
    setOriginDealId(order.originDealId || order.id)
    setStatus(order.stage?.name || '')
    setStatusId(order.stage?.id || order.statusId || 0)

    setModalActive(true)
    setModalSection('ordersDetails')
  }

  const fetchOrderDetails = async (order: PloomesOrder): Promise<OrdersDetailsDTO> => {
    const response = await axios.post(
      'https://ploomes-orders-hjk3p7rq3q-rj.a.run.app/orders-by-ploomes/details',
      {
        id: order.id,
        originDealId: order.originDealId || order.id,
      },
    )
    return response.data
  }

  const handleOpenOrderDocument = async (
    order: PloomesOrder,
    documentType: 'xml' | 'danfe' | 'certificate',
  ) => {
    try {
      const details = await fetchOrderDetails(order)
      const documentUrl =
        documentType === 'xml'
          ? details?.invoice?.xmlArray?.[0]
          : documentType === 'danfe'
            ? details?.invoice?.danfeArray?.[0]
            : details?.documents?.[0]
      if (!documentUrl) {
        setFeedback({ isOpen: true, message: 'Documento indisponível para este pedido.', statusOk: false })
        return
      }
      if (documentType === 'xml') {
        void downloadRemoteFile(documentUrl, `pedido-${order.id}.xml`)
      } else {
        void openSignedAttachment(documentUrl)
      }
    } catch (err) {
      console.error('Erro ao carregar documentos do pedido:', err)
      setFeedback({ isOpen: true, message: 'Erro ao carregar os documentos do pedido.', statusOk: false })
    }
  }

  return (
    <>
      <PageHeader
        title="Rastreio de Pedidos"
        onReload={() => setRefreshToken(prev => !prev)}
        isLoading={loading}
      />
      <Content>
        {error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            {error}
          </div>
        ) : (
          <StandardDataTable
            columns={columns}
            data={orders.slice(0, visibleCount)}
            columnsState={columnsState}
            onColumnsChange={handleColumnsChange}
            onRestoreColumns={handleRestoreColumns}
            totalCount={orders.length}
            onLoadMore={() => setVisibleCount(prev => prev + 50)}
            hasMore={visibleCount < orders.length}
            isLoading={loading}
            countLabel="pedidos"
            exportFileName="pedidos"
            emptyMessage="Nenhum pedido encontrado"
            rowActions={(order) => [
              { label: 'Ver Detalhes', icon: <Eye size={16} />, onClick: () => handleOrderClick(order) },
              { label: 'Xml', icon: <FileCode size={16} />, onClick: () => handleOpenOrderDocument(order, 'xml') },
              { label: 'Nota fiscal', icon: <FileText size={16} />, onClick: () => handleOpenOrderDocument(order, 'danfe') },
              { label: 'Certificado(s)', icon: <Award size={16} />, onClick: () => handleOpenOrderDocument(order, 'certificate') },
            ]}
          />
        )}
      </Content>
    </>
  )
}

const Orders: React.FC = () => {
  return <PloomesOrdersTable />
}

export default Orders
